import { Result1 } from '@mm-shared/types/Result1';
import {
  getResultCircleLayout,
  getResultCircleColor,
  calculateCirclePositions,
} from '../../utils/resultCircleUtils';

interface ResultCirclesSVGProps {
  results: Result1[];
  statusCode: string;
  className?: string;
  enableFill?: boolean;
  showStatusCodes?: boolean;
  /** UUID of the result to highlight. When a result's UUID matches, its stroke changes to blue-600. */
  highlightedResultUuid?: string | null;
}

export function ResultCirclesSVG({
  results,
  statusCode,
  className = '',
  enableFill = true,
  showStatusCodes = false,
  highlightedResultUuid = null,
}: ResultCirclesSVGProps) {
  if (results.length === 0) {
    return null;
  }

  // Use a normalized coordinate system (100x100) for responsiveness
  const viewBoxSize = { width: 100, height: 100 };
  const layout = getResultCircleLayout(results.length);
  const positions = calculateCirclePositions(results, viewBoxSize);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${viewBoxSize.width} ${viewBoxSize.height}`}
      className={className}
      role="img"
      aria-label={`${results.length} result${results.length > 1 ? 's' : ''}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {results.map((result, index) => (
        <g key={result.uuid}>
          <circle
            cx={positions[index].x}
            cy={positions[index].y}
            r={layout.radius}
            fill={getResultCircleColor(result, statusCode, enableFill)}
            stroke={highlightedResultUuid === result.uuid ? 'rgb(37, 99, 235)' : 'white'}
            strokeWidth={6}
            className="result-circle"
            role="presentation"
          />
          {showStatusCodes && (
            <text
              x={positions[index].x}
              y={positions[index].y}
              textAnchor="middle"
              dominantBaseline="central"
              className="text-xs font-semibold fill-white"
              style={{ fontSize: '40' }}
            >
              {result.isKeyResult ? 'M' : 'i'}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
